import { errorToast } from "@/v2-ui/toast"
import {
  ButtonHint,
  ConfirmButton,
  type ConfirmButtonProps,
} from "@/v2-backoffice-client/components/button"
import { useBasemapEnableMutation } from "@/v2-backoffice-client/basemap/enable/basemap.enable.mutation"
import type { BasemapFragment } from "@/v2-backoffice-client/basemap/__types__/basemap.fragment"

export type BasemapEnableButtonProps = Omit<ConfirmButtonProps, "onConfirm"> & {
  basemap: BasemapFragment
}

/**
 * Publishes or withdraws a basemap. Basemaps have no review queue — there is
 * no seeded-then-approved lifecycle for them — so this one switch is the
 * whole lifecycle: in the console's basemap chooser, or not.
 */
function BasemapEnableButton(props: BasemapEnableButtonProps) {
  const { basemap, children, ...buttonProps } = props

  const [toggle, { loading, error }] = useBasemapEnableMutation()
  errorToast(error, { title: "Failed to update the basemap" })

  const isEnabled = basemap.IsEnabled

  return (
    <ConfirmButton
      variant={isEnabled ? "warning" : "success"}
      confirmVariant={isEnabled ? "warning" : "success"}
      isStacked
      size="md"
      iconSrc={isEnabled ? "eye-crossed" : "eye"}
      isLoading={loading}
      content={
        isEnabled ? (
          <ButtonHint title="Take offline — leave the chooser">
            Removes this basemap from the console&apos;s basemap chooser for
            every customer in this environment.
          </ButtonHint>
        ) : (
          <ButtonHint title="Go live — show to customers in this environment">
            Offers this basemap in the console&apos;s basemap chooser right
            away, to every customer the country rules allow.
          </ButtonHint>
        )
      }
      confirmContent={
        isEnabled
          ? "Withdraw this basemap from all customers?"
          : "Show this basemap to all customers?"
      }
      onConfirm={() => {
        toggle({
          variables: {
            featureCode: basemap.FeatureCode,
            isEnabled: !isEnabled,
          },
        })
      }}
      {...buttonProps}
    >
      {children ?? (isEnabled ? "Take offline" : "Go live")}
    </ConfirmButton>
  )
}

export default BasemapEnableButton
